import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Minus, Plus } from 'lucide-react';
import { formatCurrency } from '@/lib/i18n';
import { api } from '@/lib/api';
import { localizedName } from '@/lib/localized';
import { CatalogItemType, type CatalogItem, type Modifier } from '@/Pages/Catalog/lib';
import type { CatalogItemRow } from '@/Redux/Slices/catalogApi';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Textarea } from '@/components/ui/field';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { apiError } from './lib';
import { unitPriceOf, type CartModifier, type LineDraft } from './registerCart';

/**
 * The question a tile has to ask before it becomes a cart line.
 *
 * `GET /catalog` carries only `_count: { variants, modifierGroups }`; the sizes
 * and the attached groups come from `GET /catalog/:id`, which is what that
 * endpoint is for. So this is read on demand, per product, and only for a
 * product that has something to ask about — a flat item never opens it at all.
 *
 * ## Why this exists, when the POS register has no equivalent
 *
 * The till app adds a product with sizes by a single tap: no variant, no
 * modifiers, and every ring merged into one line by item id. `pos-api.ts`'s
 * `isSimpleProduct` documents that as a known gap — "the real fix is a sheet
 * like the waiter app's `ItemConfigSheet`, and it is not this change". This is
 * that sheet. A large latte with an extra shot is a different thing to make and
 * a different thing to charge for than a small one, and the cart's identity
 * (`registerCart.lineKey`) can only be honest if something asked.
 *
 * ## What it will not do
 *
 * A **meal combo's** component groups ("main + side + drink") are a third
 * selection model with its own substitution pricing, and `order-pricing.ts`
 * answers 400 to a meal whose required group is unfilled. Rather than let a
 * cashier build a cart that is going to be refused, a meal that has component
 * groups says so and cannot be rung up here. That is a smaller capability than
 * the API has and a larger one than the POS register, which offers the tap and
 * takes the 400.
 */

/** A group as this dialog works with it: the join row flattened onto the group. */
interface Group {
  id: string;
  name: string;
  minSelect: number;
  maxSelect: number;
  isRequired: boolean;
  modifiers: Modifier[];
}

interface Props {
  item: CatalogItemRow;
  currency: string;
  onClose: () => void;
  onAdd: (draft: LineDraft) => void;
}

export default function ItemConfigDialog({ item, currency, onClose, onAdd }: Props) {
  const { t, i18n } = useTranslation();

  const [detail, setDetail] = useState<CatalogItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [variantId, setVariantId] = useState<string | null>(null);
  const [picked, setPicked] = useState<Record<string, string[]>>({});
  const [notes, setNotes] = useState('');
  const [quantity, setQuantity] = useState(1);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<CatalogItem>(`/catalog/${item.id}`)
      .then(({ data }) => {
        if (cancelled) return;
        setDetail(data);
        // A row with no `isDefault` anywhere still has to start somewhere, and
        // the zero-delta variant is the one already sitting at the base price —
        // so preselecting it cannot move the total under the cashier.
        const variants = (data.variants ?? []).filter((variant) => variant.isActive);
        const fallback =
          variants.find((variant) => variant.isDefault) ??
          variants.find((variant) => variant.priceDelta === '0' || Number(variant.priceDelta) === 0) ??
          variants[0];
        setVariantId(fallback?.id ?? null);
      })
      .catch((e) => {
        if (!cancelled) setError(apiError(e, t('billing.register.itemLoadFailed')));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [item.id, t]);

  useEffect(load, [load]);

  const variants = useMemo(
    () => (detail?.variants ?? []).filter((variant) => variant.isActive),
    [detail],
  );

  const groups = useMemo<Group[]>(
    () =>
      (detail?.modifierGroups ?? [])
        .slice()
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((attached) => ({
          id: attached.group.id,
          name: attached.group.name,
          minSelect: attached.group.minSelect,
          maxSelect: attached.group.maxSelect,
          isRequired: attached.group.isRequired,
          modifiers: (attached.group.modifiers ?? []).filter((modifier) => modifier.isActive),
        })),
    [detail],
  );

  /**
   * A meal whose components have not been chosen would be a 400 at the pass, so
   * it is refused here where the cashier can still pick something else.
   */
  const mealBlocked =
    detail?.type === CatalogItemType.MEAL_COMBO && (detail?.componentGroups?.length ?? 0) > 0;

  const chosen = useMemo<CartModifier[]>(() => {
    const out: CartModifier[] = [];
    for (const group of groups) {
      for (const id of picked[group.id] ?? []) {
        const modifier = group.modifiers.find((row) => row.id === id);
        if (modifier) out.push({ modifierId: modifier.id, name: modifier.name, price: modifier.price });
      }
    }
    return out;
  }, [groups, picked]);

  const variant = variants.find((row) => row.id === variantId) ?? null;
  const base = detail?.basePrice ?? item.basePrice;
  const unitPrice = unitPriceOf(base, variant?.priceDelta ?? null, chosen, currency);

  /** Every required group that has not been filled — the reason Add is refused. */
  const unmet = groups.filter(
    (group) => group.isRequired && (picked[group.id]?.length ?? 0) < group.minSelect,
  );

  const toggle = (group: Group, modifierId: string) => {
    setPicked((current) => {
      const held = current[group.id] ?? [];
      if (group.maxSelect <= 1) {
        // A single-choice group is a radio: picking the one already picked
        // clears it, which is the only way out of an optional single choice.
        return { ...current, [group.id]: held[0] === modifierId ? [] : [modifierId] };
      }
      if (held.includes(modifierId)) {
        return { ...current, [group.id]: held.filter((id) => id !== modifierId) };
      }
      // At the cap the oldest choice gives way, rather than the press doing
      // nothing and reading as an unresponsive screen.
      const next = held.length >= group.maxSelect ? held.slice(1) : held;
      return { ...current, [group.id]: [...next, modifierId] };
    });
  };

  const name = localizedName(detail ?? item, i18n.language);
  const displayName = variant ? `${name} — ${variant.name}` : name;

  const add = () => {
    onAdd({
      itemId: item.id,
      name: displayName,
      variantId: variant?.id ?? null,
      unitPrice,
      quantity,
      notes,
      modifiers: chosen,
    });
    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={name}
      description={t('billing.register.configureHint')}
      className="max-w-2xl"
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-3">
          <span className="text-sm">
            <span className="font-semibold tabular-nums">
              {t('billing.register.lineEach', {
                amount: formatCurrency(unitPrice, currency, i18n.language),
              })}
            </span>
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={add}
              disabled={loading || !!error || mealBlocked || unmet.length > 0}
              title={unmet.length > 0 ? unmet.map((group) => group.name).join(' · ') : undefined}
            >
              {t('billing.register.addToCart')}
            </Button>
          </div>
        </div>
      }
    >
      {loading ? (
        <LoadingState label={t('common.loading')} />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : mealBlocked ? (
        <EmptyState
          title={t('billing.register.mealUnsupported')}
          hint={t('billing.register.mealUnsupportedHint')}
        />
      ) : (
        <div className="space-y-5">
          {variants.length > 0 && (
            <fieldset>
              <legend className="mb-2 text-sm font-medium">{t('billing.register.size')}</legend>
              <div className="flex flex-wrap gap-2">
                {variants.map((row) => (
                  <Button
                    key={row.id}
                    type="button"
                    variant={row.id === variantId ? 'default' : 'outline'}
                    aria-pressed={row.id === variantId}
                    onClick={() => setVariantId(row.id)}
                  >
                    {row.name}
                  </Button>
                ))}
              </div>
            </fieldset>
          )}

          {groups.map((group) => (
            <fieldset key={group.id}>
              <legend className="mb-2 text-sm font-medium">
                {group.name}{' '}
                <span className="font-normal text-ink-muted">
                  {group.isRequired
                    ? t('billing.register.groupRequired', { count: group.minSelect })
                    : t('billing.register.groupOptional', { count: group.maxSelect })}
                </span>
              </legend>
              <div className="flex flex-wrap gap-2">
                {group.modifiers.length === 0 ? (
                  <p className="text-sm text-ink-muted">{t('billing.register.groupEmpty')}</p>
                ) : (
                  group.modifiers.map((modifier) => {
                    const on = (picked[group.id] ?? []).includes(modifier.id);
                    return (
                      <Button
                        key={modifier.id}
                        type="button"
                        variant={on ? 'default' : 'outline'}
                        aria-pressed={on}
                        onClick={() => toggle(group, modifier.id)}
                      >
                        {modifier.name}
                      </Button>
                    );
                  })
                )}
              </div>
            </fieldset>
          ))}

          <Field label={t('billing.register.lineNotes')} hint={t('billing.register.lineNotesHint')}>
            <Textarea rows={2} value={notes} onChange={(event) => setNotes(event.target.value)} />
          </Field>

          <div className="flex items-center gap-3">
            <span className="text-sm font-medium">{t('common.quantity')}</span>
            <Button
              variant="outline"
              size="sm"
              aria-label={t('billing.register.decrease')}
              disabled={quantity <= 1}
              onClick={() => setQuantity((value) => Math.max(1, value - 1))}
            >
              <Minus className="h-4 w-4" aria-hidden />
            </Button>
            <span className="w-8 text-center text-lg font-semibold tabular-nums">{quantity}</span>
            <Button
              variant="outline"
              size="sm"
              aria-label={t('billing.register.increase')}
              onClick={() => setQuantity((value) => value + 1)}
            >
              <Plus className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
